import React, { useCallback, useState } from "react";
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
} from "../../../design-system";
import { Icon } from "../../../design-system/Icon";
import { createOvertimeRequest } from "../../../helpers/kronaly_helper";
import { useTimeTracking } from "../../../hooks/useTimeTracking";
import ReportIncidentModal from "./ReportIncidentModal";

const ShiftLimitModal = ({ open, warning, employeeProfileId, onDismiss }) => {
  const { clockOut } = useTimeTracking({ poll: false, tick: false });
  const [view, setView] = useState("main");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Overtime request form
  const [reason, setReason] = useState("");
  const [estimatedMinutes, setEstimatedMinutes] = useState("");
  const [taskRef, setTaskRef] = useState("");

  // Incident modal
  const [incidentOpen, setIncidentOpen] = useState(false);

  const resetForm = useCallback(() => {
    setView("main");
    setReason("");
    setEstimatedMinutes("");
    setTaskRef("");
    setError(null);
  }, []);

  const handleClockOut = async () => {
    setBusy(true);
    setError(null);
    resetForm();
    onDismiss();
    try {
      await clockOut();
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo finalizar la jornada.");
      setBusy(false);
    }
  };

  const handleRequestOvertime = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError("Indique el motivo para continuar trabajando.");
      return;
    }
    const mins = parseInt(estimatedMinutes, 10);
    if (!Number.isInteger(mins) || mins < 1) {
      setError("Indique el tiempo estimado adicional en minutos.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const note = [
        `Motivo: ${reason.trim()}`,
        `Tiempo estimado: ${mins} min`,
        taskRef.trim() ? `Tarea/Cliente: ${taskRef.trim()}` : null,
      ].filter(Boolean).join("\n");

      await createOvertimeRequest({
        employee_profile_id: employeeProfileId,
        minutes_requested: mins,
        note,
      });
      resetForm();
      onDismiss();
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo enviar la solicitud.");
    } finally {
      setBusy(false);
    }
  };

  const handleIncidentCreated = () => {
    setIncidentOpen(false);
    resetForm();
    onDismiss();
  };

  if (!open) return null;

  return (
    <>
      <Modal
        open={open && !incidentOpen}
        onClose={() => {}}
        title="Jornada ordinaria alcanzada"
        size="md"
      >
        {view === "main" ? (
          <>
            <ModalBody>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                  background: "var(--kr-status-danger-bg)", display: "flex",
                  alignItems: "center", justifyContent: "center",
                }}>
                  <Icon name="AlarmClock" size={20} color="var(--kr-danger-700)" />
                </div>
                <p className="kr-fs-13" style={{ margin: 0, lineHeight: 1.5 }}>
                  {warning?.message || "Ya has cumplido con las horas establecidas para hoy. Debes finalizar tu jornada o solicitar horas extras. Si no realizas ninguna acción en los próximos 30 minutos, el sistema cerrará tu turno de forma automática."}
                </p>
              </div>
            </ModalBody>
            <ModalFooter style={{ flexDirection: "column", gap: 8 }}>
              <Button
                variant="primary"
                onClick={handleClockOut}
                disabled={busy}
                style={{ width: "100%" }}
              >
                {busy && <Spinner size={12} color="#fff" />}
                Finalizar jornada
              </Button>
              <Button
                variant="secondary"
                onClick={() => { setView("request"); setError(null); }}
                disabled={busy}
                style={{ width: "100%" }}
              >
                Solicitar autorización
              </Button>
              <Button
                variant="secondary"
                onClick={() => { setIncidentOpen(true); setError(null); }}
                disabled={busy}
                style={{ width: "100%" }}
              >
                Registrar incidencia
              </Button>
            </ModalFooter>
          </>
        ) : (
          <form onSubmit={handleRequestOvertime}>
            <ModalBody>
              {error && <div className="kr-mb-3"><Alert tone="danger">{error}</Alert></div>}
              <p className="kr-text-muted kr-fs-12 kr-mb-4">
                Indique el motivo por el que necesita continuar trabajando. Su solicitud
                será enviada a su responsable para su aprobación.
              </p>
              <Field label="Motivo" style={{ marginBottom: 12 }}>
                <Input
                  type="textarea"
                  rows={2}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej: Cierre de expediente urgente"
                  required
                />
              </Field>
              <Field label="Tiempo estimado adicional (minutos)" style={{ marginBottom: 12 }}>
                <Input
                  type="number"
                  min={1}
                  value={estimatedMinutes}
                  onChange={(e) => setEstimatedMinutes(e.target.value)}
                  placeholder="Ej: 30"
                  required
                />
              </Field>
              <Field label="Tarea / Cliente (opcional)">
                <Input
                  value={taskRef}
                  onChange={(e) => setTaskRef(e.target.value)}
                  placeholder="Ej: Proyecto ACME, Exp. 2024/123"
                  maxLength={200}
                />
              </Field>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" type="button" onClick={() => setView("main")}>
                Volver
              </Button>
              <Button variant="primary" type="submit" disabled={busy}>
                {busy && <Spinner size={12} color="#fff" />}
                Enviar solicitud
              </Button>
            </ModalFooter>
          </form>
        )}
      </Modal>

      <ReportIncidentModal
        open={incidentOpen}
        employeeProfileId={employeeProfileId}
        onClose={() => setIncidentOpen(false)}
        onCreated={handleIncidentCreated}
      />
    </>
  );
};

export default ShiftLimitModal;
